// @flow
import * as React from 'react';
import { EnhancedField } from 'common/components/core/form/Field';
import { Column, Columns, Stack } from '@locus-taxy/neo-ui';
import Section from './Section';
import { VehicleCompartment } from 'common/models/internal/VehicleCompartment';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import type { TFunction } from 'react-i18next';
import type { ThemeType } from 'react-css-themr';
import { VolumeUnitEnum } from 'common/enums/VolumeUnitEnum';
import { units } from './units';

type Props = {
    theme: ThemeType,
    compartment: VehicleCompartment,
    onChange: (accessor: string, value: string) => void,
    unitConverter: UnitConversionHelper,
    t: TFunction
};

export const CompartmentCapacity = ({
    theme,
    compartment,
    onChange,
    unitConverter,
    t
}: Props): React.Node => {
    const volumeUnit =
        compartment.volume.unit === VolumeUnitEnum.MetersCubic
            ? unitConverter.getConvertedUnitDisplayText({
                  preferredUnits: units.volume.preferredUnits,
                  sourceUnit: units.volume.sourceUnit,
                  pluralizeUnit: false
              })
            : '';

    const dimensionUnit = unitConverter.getConvertedUnitDisplayText({
        preferredUnits: units.dimensions.preferredUnits,
        sourceUnit: units.dimensions.sourceUnit,
        pluralizeUnit: false
    });

    return (
        <Section
            title={t('masterVehicleModelProperties.compartments.capacity')}
            id="container-capacity"
        >
            <Columns space="small">
                <Column>
                    <Stack>
                        <span className={theme.label}>
                            {t(
                                'masterVehicleModelProperties.compartments.dimensions.length'
                            )}
                        </span>
                        <EnhancedField
                            type="text"
                            element="input"
                            value={compartment.dimensions.length}
                            onChange={value =>
                                onChange('dimensions.length', value)
                            }
                            testId="length"
                            fieldId="length"
                            placeholder={t(
                                'masterVehicleModelProperties.compartments.dimensions.length'
                            )}
                            rightIcon={dimensionUnit}
                            theme={theme}
                            error={compartment.dimensions.getValidationMessage(
                                'length'
                            )}
                        />
                    </Stack>
                </Column>
                <Column>
                    <Stack>
                        <span className={theme.label}>
                            {t(
                                'masterVehicleModelProperties.compartments.dimensions.breadth'
                            )}
                        </span>
                        <EnhancedField
                            type="text"
                            element="input"
                            value={compartment.dimensions.breadth}
                            onChange={value =>
                                onChange('dimensions.breadth', value)
                            }
                            testId="breadth"
                            fieldId="breadth"
                            placeholder={t(
                                'masterVehicleModelProperties.compartments.dimensions.breadth'
                            )}
                            rightIcon={dimensionUnit}
                            theme={theme}
                            error={compartment.dimensions.getValidationMessage(
                                'breadth'
                            )}
                        />
                    </Stack>
                </Column>
                <Column>
                    <Stack>
                        <span className={theme.label}>
                            {t(
                                'masterVehicleModelProperties.compartments.dimensions.height'
                            )}
                        </span>
                        <EnhancedField
                            type="text"
                            element="input"
                            value={compartment.dimensions.height}
                            onChange={value =>
                                onChange('dimensions.height', value)
                            }
                            testId="height"
                            fieldId="height"
                            placeholder={t(
                                'masterVehicleModelProperties.compartments.dimensions.height'
                            )}
                            rightIcon={dimensionUnit}
                            theme={theme}
                            error={compartment.dimensions.getValidationMessage(
                                'height'
                            )}
                        />
                    </Stack>
                </Column>
            </Columns>
            <Columns space="small">
                <Column width="3/5">
                    <Stack>
                        <span className={theme.label}>{`${t(
                            'masterVehicleModelProperties.compartments.volume'
                        )} *`}</span>
                        <EnhancedField
                            type="text"
                            element="input"
                            value={compartment.volume.value}
                            onChange={value => onChange('volume.value', value)}
                            testId="volume"
                            fieldId="breadth"
                            placeholder={t(
                                'masterVehicleModelProperties.compartments.volume'
                            )}
                            rightIcon={volumeUnit}
                            theme={theme}
                            error={compartment.volume.getValidationMessage(
                                'value'
                            )}
                        />
                    </Stack>
                </Column>
            </Columns>
        </Section>
    );
};

export default CompartmentCapacity;
